import BodyText from 'LuxKit/Typography/BodyText'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import useCruiseSearchCount from 'hooks/Cruise/useCruiseSearchCount'
import useCruiseSearchFlashOffers from 'hooks/Cruise/useCruiseSearchFlashOffers'
import { EmptyArray } from 'lib/array/arrayUtils'
import React, { useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { getCruiseOfferFiltersFromParams } from 'lib/cruises/cruiseUtils'
import useQueryParams from 'hooks/useQueryParams'

interface Props {
  initialFilters?: App.OfferListFilters
  shouldIgnoreFlashOffers?: boolean
}

function FormattedCruiseLineSearchSummary({ initialFilters, shouldIgnoreFlashOffers }: Props) {
  const intl = useIntl()
  const queryParams = useQueryParams()
  const globalFilters = useContext(GlobalSearchStateContext)
  const {
    cruiseLines,
    searchItems,
    secondarySearchItems,
    durationMax,
    durationMin,
    flexibleMonths,
    checkinDate,
    checkoutDate,
    durationRange,
  } = globalFilters
  const [filters] = useMemo(() => getCruiseOfferFiltersFromParams(queryParams), [queryParams])

  const [searchCount, fetchingSearchCount] = useCruiseSearchCount({
    ...filters,
    cruiseLines,
    destinationIds: searchItems.map(({ value }) => value),
    departureIds: secondarySearchItems.map(({ value }) => value),
    durationMin,
    durationMax,
    durationRange,
    departureMonths: flexibleMonths?.split(',') || EmptyArray,
    ...(!!checkinDate && !!checkoutDate && {
      departureStartDate: checkinDate.format(ISO_DATE_FORMAT),
      departureEndDate: checkoutDate.format(ISO_DATE_FORMAT),
    }),
    ...(initialFilters || {}),
  })
  const flashOffers = useCruiseSearchFlashOffers({
    filters,
    globalFilters,
  })

  const count = shouldIgnoreFlashOffers ? searchCount : searchCount + (flashOffers.offerCount ?? 0)

  return <BodyText data-testid="cruise-filter-footer-available-results" variant="medium">
    {!fetchingSearchCount && intl.formatMessage(
      {
        id: 'rj2h5NJJez',
        defaultMessage: '{count, plural, one {# cruise} other {# cruises}} available',
        description: 'Count of available cruises in cruise line search summary',
      },
      { count },
    )}
    {fetchingSearchCount && <TextLoadingBox width="16ch" />}
  </BodyText>
}

export default FormattedCruiseLineSearchSummary
